import { Link } from '@tanstack/react-router'

import { cn } from '@gto/ui/cn'
import { Typography } from '@gto/ui/typography'

import { useMe } from '@/auth/me'
import { ADMIN_SECTIONS, type AdminSection, canViewSection } from '@/auth/sections'

const CARD_CLASS =
  'flex flex-col gap-3 rounded-md border border-divider bg-paper-elevation2 p-panel'

const ICON_CLASS =
  'flex size-12 items-center justify-center rounded-md bg-paper-elevation0 text-text-secondary'

const SectionCard = ({ section }: { section: AdminSection }) => {
  const { homeCard } = section

  return (
    <Link
      data-tst={homeCard.dataTst}
      to={homeCard.to}
      className={cn(
        CARD_CLASS,
        'group no-underline transition-colors hover:border-primary-main hover:bg-paper-elevation3',
      )}
    >
      <span className={cn(ICON_CLASS, 'transition-colors group-hover:text-primary-main')}>
        <homeCard.Icon className="size-6" />
      </span>
      <div className="flex flex-col gap-1">
        <Typography
          color="text.primary"
          variant="subtitle1"
        >
          {section.heading}
        </Typography>
        <Typography
          color="text.secondary"
          variant="body2"
        >
          {homeCard.description}
        </Typography>
      </div>
    </Link>
  )
}

export function AdminHome() {
  const me = useMe()
  const sections = ADMIN_SECTIONS.filter((section) => canViewSection(me, section))

  return (
    <div
      className="mx-auto flex w-full max-w-5xl flex-col gap-6 p-4"
      data-tst="admin-home"
    >
      <div className="flex flex-col gap-1">
        <Typography
          className="font-semibold"
          color="text.primary"
          variant="h4"
        >
          Admin
        </Typography>
        <Typography
          color="text.secondary"
          variant="body1"
        >
          Pick a surface to work in.
        </Typography>
      </div>

      {sections.length === 0 && (
        <Typography
          color="text.secondary"
          data-tst="admin-home-empty"
          variant="body2"
        >
          Your account cannot open any admin section yet. Ask an admin to grant you a permission.
        </Typography>
      )}

      <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-4">
        {sections.map((section) => (
          <SectionCard
            key={section.id}
            section={section}
          />
        ))}
      </div>
    </div>
  )
}
